import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { Lock, X } from 'lucide-react';
import { formatBRMoney, parseBRMoney, PAYMENT_METHODS, paymentToneColors, useStandaloneFieldSx } from '../shared';
import { useTheme } from '@mui/material/styles';
import { useCloseCash } from './useCashActions';

function diffColor(theme, diff) {
    if (Math.abs(diff) < 0.005) return theme.palette.text.disabled;
    return diff > 0 ? theme.palette.success.main : theme.palette.error.main;
}

function diffLabel(diff) {
    if (Math.abs(diff) < 0.005) return `R$ ${formatBRMoney(0)}`;
    return `${diff > 0 ? '+ ' : '− '}R$ ${formatBRMoney(Math.abs(diff))}`;
}

// `DatePicker` (MUI X) usa classes `MuiPickersOutlinedInput-*` próprias, não
// as `MuiOutlinedInput-*` que o override do theme cobre. Mesma caixa do
// `ffs` (52px, radius 12px, fundo `background.subtle`, borda 1.5px) dos
// demais campos do drawer (Observação) — o label continua estático acima
// (`fieldLabelSx`, igual ao `Dropdown` `static-micro`), largura fluida para
// caber duas datas lado a lado.
const dateFieldSx = {
    width: '100%',
    '& .MuiPickersOutlinedInput-root': {
        height: 52,
        padding: '0 14px',
        borderRadius: '12px',
        backgroundColor: 'background.subtle',
    },
    '& .MuiPickersOutlinedInput-notchedOutline': {
        borderWidth: '1.5px',
        borderColor: 'divider',
    },
    '& .MuiPickersOutlinedInput-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
        borderWidth: '1.5px',
    },
    '& .MuiPickersSectionList-sectionContent, & .MuiPickersInputBase-sectionsContainer': {
        fontSize: '13.5px',
    },
};

// Label estático uppercase (`Dropdown` `labelVariant="static-micro"`).
const fieldLabelSx = { fontSize: '10.5px', textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.secondary', fontWeight: 800, mb: '6px' };

// Fechar caixa — drawer lateral de conciliação por forma de pagamento
// (DESIGN-SYSTEM.md §5.6 "multi-step 'complete this transaction' action"),
// mesmo padrão de `drawer-overlay`/`drawer-box`/`drawer-head`/`drawer-body`/
// `drawer-foot` do "Formulário de caixa". `recon-table`: linha por forma de
// pagamento (§ ver `shared.js` — 3 formas agregadas, não as 4 do protótipo,
// gap de backend documentado ali), "Esperado" somente leitura vindo de
// `expected` (calculado pelo chamador a partir de `GET /cashregister/{id}/
// resume`), "Contado" editável pré-preenchido com o esperado, "Diferença"
// recalculada ao vivo. Endpoint real: `POST /cashregister/{id}/close`
// (`cashregister/closer.go`), payload `{total_amount_credit,
// total_amount_debit, total_amount_pix, total_amount_cash, start_date,
// end_date}` — como o backend nunca separa débito/crédito em lugar nenhum
// (nem para persistir, só soma os 4 para validar o total contado contra o
// total vendido), o valor de "Cartão" é enviado inteiro como
// `total_amount_credit` e `total_amount_debit` fixo em "0,00" — não afeta
// nenhum dado real, só a soma usada na validação.
//
// Período (`start_date`/`end_date`) editável: por padrão início = abertura da
// sessão e fim = hoje (`expected` do chamador). Ao mudar qualquer data, o
// "Esperado" de Pix/Cartão é rebuscado em `GET /cashregister/{id}/resume?
// startDate=&endDate=` — o backend valida o total contado contra as vendas
// desse mesmo período. "Dinheiro" continua vindo de `expected` (saldo físico
// do caixa, `available_cash`, não depende do período).
export default function CloseCashDrawer({ open, onClose, cashRegister, sessionStart, expected, onSuccess, onError }) {
    const theme = useTheme();
    const standaloneFieldSx = useStandaloneFieldSx();
    const {
        counted, changeCounted, observation, setObservation, saving, startDate, setStartDate, endDate, setEndDate,
        loadingPeriod, periodError, rangeError, effectiveExpected, totals, confirm: handleConfirm,
    } = useCloseCash({ open, cashRegister, sessionStart, expected, onSuccess, onError });

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={saving ? undefined : onClose}
            PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, maxWidth: '100vw', display: 'flex', flexDirection: 'column' } }}
        >
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: '22px 22px 16px', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5, flexShrink: 0 }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ width: 38, height: 38, borderRadius: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: '10px', bgcolor: 'background.subtle', color: 'text.secondary' }}>
                            <Lock size={18} strokeWidth={2} />
                        </Box>
                        <Typography sx={{ fontSize: 17, fontWeight: 800, mb: '4px' }}>Fechar Caixa</Typography>
                        <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                            {cashRegister ? `Confira os valores por forma de pagamento e finalize a sessão de "${cashRegister.name}".` : ''}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small" disabled={saving} aria-label="Fechar">
                        <X size={18} strokeWidth={2} />
                    </IconButton>
                </Box>

                <Box sx={{ p: '20px 22px', flex: 1, overflowY: 'auto' }}>
                    <Box sx={{ mb: '18px', p: '14px 16px', bgcolor: 'background.subtle', border: '1px solid', borderColor: 'divider', borderRadius: '12px' }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Box sx={{ flex: 1, minWidth: 150 }}>
                                    <Typography sx={fieldLabelSx}>Início</Typography>
                                    <DatePicker
                                        format="DD/MM/YYYY"
                                        value={startDate}
                                        onChange={(value) => setStartDate(value)}
                                        maxDate={endDate?.isValid() ? endDate : dayjs()}
                                        disabled={saving}
                                        slotProps={{ textField: { size: 'small', sx: dateFieldSx } }}
                                    />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 150 }}>
                                    <Typography sx={fieldLabelSx}>Fim</Typography>
                                    <DatePicker
                                        format="DD/MM/YYYY"
                                        value={endDate}
                                        onChange={(value) => setEndDate(value)}
                                        minDate={startDate?.isValid() ? startDate : undefined}
                                        maxDate={dayjs()}
                                        disabled={saving}
                                        slotProps={{ textField: { size: 'small', sx: dateFieldSx } }}
                                    />
                                </Box>
                            </Box>
                        </LocalizationProvider>
                        {(rangeError || periodError) ? (
                            <Typography sx={{ fontSize: 11.5, color: 'error.main', fontWeight: 600, mt: '8px' }}>
                                {rangeError || periodError}
                            </Typography>
                        ) : (
                            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: '8px' }}>
                                Sessão aberta em {sessionStart?.label || '—'}. Os valores esperados consideram as vendas do período escolhido.
                            </Typography>
                        )}
                    </Box>

                    <Table size="small" sx={{ mb: '4px' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ border: 'none', pl: 0, pr: 1, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.04em', color: 'text.disabled', fontWeight: 700 }}>Forma</TableCell>
                                <TableCell align="right" sx={{ border: 'none', px: 1, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.04em', color: 'text.disabled', fontWeight: 700, minWidth: 84 }}>Esperado</TableCell>
                                <TableCell align="right" sx={{ border: 'none', px: 1, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.04em', color: 'text.disabled', fontWeight: 700, minWidth: 126 }}>Contado</TableCell>
                                <TableCell align="right" sx={{ border: 'none', pl: 1, pr: 0, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.04em', color: 'text.disabled', fontWeight: 700, minWidth: 90 }}>Diferença</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {PAYMENT_METHODS.map((m) => {
                                const { bg, fg } = paymentToneColors(theme, m.tone);
                                const Icon = m.icon;
                                const exp = effectiveExpected[m.key] || 0;
                                const countedNum = parseBRMoney(counted[m.key]);
                                const diff = countedNum - exp;
                                return (
                                    <TableRow key={m.key} sx={{ '& td': { borderBottom: '1px solid', borderColor: 'divider', py: '9px', px: 0 } }}>
                                        <TableCell sx={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'text.primary', fontWeight: 700, fontSize: '12.5px', border: 'none !important' }}>
                                            <Box sx={{ width: 26, height: 26, borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, bgcolor: bg, color: fg }}>
                                                <Icon size={13} strokeWidth={2} />
                                            </Box>
                                            {m.label}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: 'text.disabled', fontSize: '12.5px' }}>
                                            R$ {formatBRMoney(exp)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontSize: '12.5px' }}>
                                            <InputBase
                                                value={counted[m.key] || ''}
                                                onChange={(e) => changeCounted(m.key, e.target.value)}
                                                sx={{
                                                    width: 110,
                                                    height: 34,
                                                    borderRadius: '8px',
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    bgcolor: 'background.subtle',
                                                    px: '10px',
                                                    fontSize: '12.5px',
                                                    textAlign: 'right',
                                                    '& input': { textAlign: 'right' },
                                                    '&.Mui-focused': { borderColor: 'primary.main', bgcolor: 'background.paper' },
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 800, fontSize: '12.5px', color: diffColor(theme, diff) }}>
                                            {diffLabel(diff)}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: '12px', borderTop: '2px solid', borderColor: 'divider', fontWeight: 800, fontSize: '13.5px', mb: '20px' }}>
                        <Typography sx={{ fontWeight: 800, fontSize: '13.5px' }}>Total</Typography>
                        <Box sx={{ display: 'flex', gap: '18px' }}>
                            <Typography sx={{ fontWeight: 800, fontSize: '13.5px' }}>R$ {formatBRMoney(totals.totalExp)}</Typography>
                            <Typography sx={{ fontWeight: 800, fontSize: '13.5px' }}>R$ {formatBRMoney(totals.totalCount)}</Typography>
                            <Typography sx={{ fontWeight: 800, fontSize: '13.5px', color: diffColor(theme, totals.diff) }}>{diffLabel(totals.diff)}</Typography>
                        </Box>
                    </Box>

                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Observação"
                        value={observation}
                        onChange={(e) => setObservation(e.target.value)}
                        variant="outlined"
                        sx={standaloneFieldSx}
                    />
                </Box>

                <Box sx={{ p: '16px 22px 20px', borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1.25, justifyContent: 'flex-end', flexShrink: 0 }}>
                    <Button variant="outlined" onClick={onClose} disabled={saving}>Cancelar</Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirm}
                        disabled={saving || loadingPeriod || Boolean(rangeError) || Boolean(periodError)}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
                    >
                        {saving ? 'Fechando...' : 'Confirmar fechamento'}
                    </Button>
                </Box>
            </Box>
        </Drawer>
    );
}
